<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';

/**
 * Slim progress bar pinned to the top of the viewport that tracks
 * route navigation — covers the gap while a lazy-loaded page chunk
 * downloads, so a click always produces immediate visual feedback.
 */

const router = useRouter();

const visible = ref(false);
const progress = ref(0);

// Delay before the bar appears. Instant/cached navigations resolve
// before this elapses, so the bar never flashes for them.
const SHOW_DELAY = 120;
// Time the bar holds at 100% before fading out (matches CSS fade).
const FINISH_DELAY = 280;

let showTimer: ReturnType<typeof setTimeout> | null = null;
let trickleTimer: ReturnType<typeof setInterval> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

function clearTimers() {
    if (showTimer) clearTimeout(showTimer);
    if (trickleTimer) clearInterval(trickleTimer);
    if (hideTimer) clearTimeout(hideTimer);
    showTimer = trickleTimer = hideTimer = null;
}

function start() {
    clearTimers();
    visible.value = false;
    progress.value = 0;

    showTimer = setTimeout(() => {
        showTimer = null;
        visible.value = true;
        progress.value = 12;
        // Trickle toward 90% — decelerating, never completing on its
        // own. Only afterEach/onError pushes the bar to 100%.
        trickleTimer = setInterval(() => {
            const remaining = 90 - progress.value;
            if (remaining > 0) {
                progress.value += Math.max(remaining * 0.12, 0.6);
            }
        }, 240);
    }, SHOW_DELAY);
}

function finish() {
    // Navigation resolved before the bar was ever shown — nothing to do.
    if (showTimer) {
        clearTimers();
        return;
    }
    if (trickleTimer) {
        clearInterval(trickleTimer);
        trickleTimer = null;
    }
    if (!visible.value) return;

    progress.value = 100;
    hideTimer = setTimeout(() => {
        hideTimer = null;
        visible.value = false;
        progress.value = 0;
    }, FINISH_DELAY);
}

let removeBefore: (() => void) | null = null;
let removeAfter: (() => void) | null = null;
let removeError: (() => void) | null = null;

onMounted(() => {
    removeBefore = router.beforeEach(() => {
        start();
        return true;
    });
    removeAfter = router.afterEach(() => finish());
    removeError = router.onError(() => finish());
});

onUnmounted(() => {
    clearTimers();
    removeBefore?.();
    removeAfter?.();
    removeError?.();
});
</script>

<template>
    <div class="nav-progress" :data-active="visible" aria-hidden="true">
        <div class="nav-progress__bar" :style="{ width: `${progress}%` }" />
    </div>
</template>
